import axios, { AxiosProgressEvent } from 'axios';
import {
  AidaAgent,
  AidaAgentCreate,
  AidaAgentUpdate,
  AidaTool,
  AidaToolCreate,
  AidaToolUpdate,
  FileResponse,
  AidaAgentConnection,
  AidaAgentConnectionPostReq,
  AidaAgentConnectionPutReq,
  AidaOrganizationBasic,
  EmbeddedChatConnection,
  WhatsappConnection,
  whatsappConnectionRequest,
  WhatsappBusinessConnectionRequest,
  whatsappConnectionResponse,
  ConversationMessage,
  ChatMode,
  UserACL,
  UserACLPostReq,
  UserACLPutReq,
  AidaOrganizationRole,
  AidaOrganizationRolePostReq,
  AidaOrganizationRolePutReq,
  AidaQuotaOrganization,
  AgentToolRelationship,
  AidaChatRequestV2,
  AidaChatResponse,
  CreateDatastoreRequest,
  CreateDatastoreResponse,
  UploadFileRequest,
  UploadFileResponse,
  ListDatastoreFilesResponse,
  DeleteDatastoreFileResponse,
  DeleteDatastoreResponse,
  AidaConversationSummary,
} from '../types';
import config from '../config';

console.log('API Host:', config.apiHost);

const apiClientV1 = axios.create({
  baseURL: `${config.apiHost}/api/v1`,
  timeout: 120000,
});

const apiClient = axios.create({
  baseURL: `${config.apiHost}/api/v2`,
  timeout: 120000,
});

const apiChannelsClient = axios.create({
  baseURL: `${config.apiHost}/api/v1`,
  timeout: 120000,
});

const apiChannelsClientV2 = axios.create({
  baseURL: `${config.apiHost}/api/v2`,
  timeout: 120000,
});

const addAuthInterceptor = (client: any) => {
  client.interceptors.request.use((reqConfig: any) => {
    const googleIdToken = localStorage.getItem('googleIdToken');
    if (googleIdToken) {
      reqConfig.headers['Authorization'] = `Bearer ${googleIdToken}`;
    }
    return reqConfig;
  });
};

addAuthInterceptor(apiClientV1);
addAuthInterceptor(apiClient);
addAuthInterceptor(apiChannelsClient);
addAuthInterceptor(apiChannelsClientV2);

const handleApiError = (error: any) => {
  if (error.response && error.response.status === 401) {
    // Token is invalid or expired. Forcing re-login.
    localStorage.removeItem('googleIdToken');
    window.dispatchEvent(new Event('auth-error'));
  }
  return Promise.reject(error);
};

apiClient.interceptors.response.use((response: any) => response, handleApiError);
apiClientV1.interceptors.response.use((response: any) => response, handleApiError);
apiChannelsClient.interceptors.response.use((response: any) => response, handleApiError);
apiChannelsClientV2.interceptors.response.use((response: any) => response, handleApiError);


export type {
  AidaAgent,
  AidaAgentCreate,
  AidaAgentUpdate,
  AidaTool,
  AidaToolCreate,
  AidaToolUpdate,
  FileResponse,
  AidaAgentConnection,
  AidaAgentConnectionPostReq,
  AidaAgentConnectionPutReq,
  AidaOrganizationBasic,
  EmbeddedChatConnection,
  WhatsappConnection,
  CreateDatastoreRequest,
  CreateDatastoreResponse,
  UploadFileRequest,
  UploadFileResponse,
  ListDatastoreFilesResponse,
  DeleteDatastoreFileResponse,
  DeleteDatastoreResponse,
};


class ApiClient {
  private static instance: ApiClient;

  private constructor() { }

  public static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  private getHeaders(organizationId?: string) {
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      'x-api-key': config.apiKey,
    };

    if (organizationId) {
      headers['x-aida-org-id'] = organizationId;
    }

    return headers;
  }

  private getPublicHeaders(organizationId?: string) {
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      'x-api-key': config.apiKey,
    };

    if (organizationId) {
      headers['x-aida-org-id'] = organizationId;
    }

    return headers;
  }

  public async getOrganizations(userEmail: string): Promise<AidaOrganizationBasic[]> {
    const response = await apiClient.get(`/user-acl/organizations/${userEmail}`);
    return response.data;
  }

  public async getAgents(organizationId: string, parentId?: string): Promise<AidaAgent[]> {
    const response = await apiClient.get('/agents', {
      headers: this.getHeaders(organizationId),
      params: { parent_id: parentId },
    });
    return response.data;
  }

  public async createAgent(organizationId: string, agent: AidaAgentCreate): Promise<AidaAgent> {
    const response = await apiClient.post('/agents', agent, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async getAgentById(organizationId: string, agentId: string): Promise<AidaAgent> {
    const response = await apiClient.get(`/agents/${agentId}`, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async getPublicAgentById(organizationId: string, agentId: string): Promise<AidaAgent> {
    const response = await apiClient.get(`/agents/${agentId}`, {
      headers: this.getPublicHeaders(organizationId),
    });
    return response.data;
  }


  public async updateAgent(organizationId: string, agentId: string, agent: AidaAgentUpdate): Promise<AidaAgent> {
    const response = await apiClient.put(`/agents/${agentId}`, agent, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async deleteAgent(organizationId: string, agentId: string): Promise<void> {
    await apiClient.delete(`/agents/${agentId}`, {
      headers: this.getHeaders(organizationId),
    });
  }

  public async getChatSessions(organizationId: string, agentId: string, userId: string, hoursAgo: number): Promise<any[]> {
    const response = await apiClient.get(`/agents/${agentId}/chat-sessions`, {
      headers: this.getHeaders(organizationId),
      params: {
        user_id: userId,
        hours_ago: hoursAgo,
      },
    });
    return response.data;
  }

  public async deleteChatSession(organizationId: string, agentId: string, sessionId: string): Promise<void> {
    await apiClient.delete(`/agents/${agentId}/chat-sessions/${sessionId}`, {
      headers: this.getHeaders(organizationId),
    });
  }

  public async getTools(organizationId: string): Promise<AidaTool[]> {
    const response = await apiClient.get('/tools', {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async createTool(organizationId: string, tool: AidaToolCreate): Promise<AidaTool> {
    const response = await apiClient.post('/tools', tool, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async updateTool(organizationId: string, toolId: string, tool: AidaToolUpdate): Promise<AidaTool> {
    const response = await apiClient.put(`/tools/${toolId}`, tool, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async deleteTool(organizationId: string, toolId: string): Promise<void> {
    await apiClient.delete(`/tools/${toolId}`, {
      headers: this.getHeaders(organizationId),
    });
  }

  public async getAgentTools(organizationId: string, agentId: string): Promise<AgentToolRelationship[]> {
    const response = await apiClient.get(`/agents/${agentId}/tools`, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async getToolById(organizationId: string, toolId: string): Promise<AidaTool> {
    const response = await apiClient.get(`/tools/${toolId}`, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async getToolByIdOrNull(organizationId: string, toolId: string): Promise<AidaTool | null> {
    try {
      const response = await apiClient.get(`/tools/${toolId}`, {
        headers: this.getHeaders(organizationId),
      });
      return response.data;
    } catch (error) {
      console.warn(`Could not fetch tool with id ${toolId}`, error);
      return null;
    }
  }

  public async addAgentTool(organizationId: string, agentId: string, toolId: string): Promise<AidaTool> {
    const response = await apiClient.post(`/agents/${agentId}/tools`, { tool_id: toolId }, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async removeAgentTool(organizationId: string, agentId: string, toolId: string): Promise<void> {
    await apiClient.delete(`/agents/${agentId}/tools/${toolId}`, {
      headers: this.getHeaders(organizationId),
    });
  }

  public async createDatastore(organizationId: string, toolId: string): Promise<CreateDatastoreResponse> {
    const response = await apiClient.post('/datastores', { tool_id: toolId }, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async getDatastoreFiles(organizationId: string, datastoreId: string): Promise<ListDatastoreFilesResponse> {
    const response = await apiClient.get(`/datastores/${datastoreId}/files`, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async uploadFileToDatastore(organizationId: string, datastoreId: string, fileUrl: string): Promise<UploadFileResponse> {
    const response = await apiClient.post(`/datastores/${datastoreId}/files`, { file_url: fileUrl }, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async deleteDatastoreFile(organizationId: string, datastoreId: string, documentId: string): Promise<DeleteDatastoreFileResponse> {
    const response = await apiClient.delete(`/datastores/${datastoreId}/files/${documentId}`, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async deleteDatastore(organizationId: string, datastoreId: string): Promise<DeleteDatastoreResponse> {
    const response = await apiClient.delete(`/datastores/${datastoreId}`, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async uploadFile(organizationId: string, datastoreId: string, file: File, onUploadProgress?: (progressEvent: AxiosProgressEvent) => void): Promise<FileResponse> {
    const bucket = config.fileUploadBucket;
    const path = `${datastoreId}/${file.name}`;

    const formData = new FormData();
    formData.append('bucket', bucket);
    formData.append('path', path);
    formData.append('file', file);

    const response = await apiClientV1.post('/files', formData, {
      headers: {
        ...this.getHeaders(organizationId),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
    return response.data;
  }

  public async uploadTemplateMedia(organizationId: string, file: File, onUploadProgress?: (progressEvent: AxiosProgressEvent) => void): Promise<FileResponse> {
    const bucket = process.env.NODE_ENV === 'production'
      ? 'aida-mx-prod-conversacion-files'
      : 'aida-mx-dev-conversacion-files';

    const timestamp = new Date().toISOString().replace(/[-:.]/g, '');
    const path = `${organizationId}/${timestamp}_${file.name}`;

    const formData = new FormData();
    formData.append('bucket', bucket);
    formData.append('path', path);
    formData.append('file', file);

    const response = await apiClientV1.post('/files', formData, {
      headers: {
        ...this.getHeaders(organizationId),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
    return response.data;
  }

  public async getConnections(organizationId: string, isPublic = false): Promise<AidaAgentConnection[]> {
    const response = await apiClient.get('/connections', {
      headers: isPublic ? this.getPublicHeaders(organizationId) : this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async createConnection(organizationId: string, connection: AidaAgentConnectionPostReq): Promise<AidaAgentConnection> {
    const response = await apiClient.post('/connections', connection, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async deleteConnection(organizationId: string, connectionId: string): Promise<void> {
    await apiClient.delete(`/connections/${connectionId}`, {
      headers: this.getHeaders(organizationId),
    });
  }

  public async updateConnection(organizationId: string, connectionId: string, connection: AidaAgentConnectionPutReq): Promise<AidaAgentConnection> {
    const response = await apiClient.put(`/connections/${connectionId}`, connection, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async completeConnectionAccount(organizationId: string, data: whatsappConnectionRequest): Promise<whatsappConnectionResponse> {
    const response = await apiClient.post(`/integrations-whatsapp/connection-account`, data, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async completeConnectionBusiness(organizationId: string, data: WhatsappBusinessConnectionRequest): Promise<whatsappConnectionResponse> {
    const response = await apiClient.post(`/integrations-whatsapp/connection-business`, data, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async getConversationHistory(organizationId: string, agentId: string): Promise<ConversationMessage[]> {
    const response = await apiChannelsClient.get('/conversation/history', {
      headers: this.getHeaders(organizationId),
      params: { agentId },
    });
    return response.data;
  }

  public async getConversations(organizationId: string, agentId: string, page: number, pageSize: number, signal?: AbortSignal): Promise<AidaConversationSummary[]> {
    const response = await apiChannelsClient.get('/conversation', {
      headers: this.getHeaders(organizationId),
      params: { agentId, page, pageSize },
      signal,
    });
    return response.data;
  }

  public async getConversationMessages(organizationId: string, agentId: string, userId: string, page: number, pageSize: number, signal?: AbortSignal): Promise<ConversationMessage[]> {
    const response = await apiChannelsClient.get(`/conversation/${agentId}/${userId}`,
      {
        headers: this.getHeaders(organizationId),
        params: { page, pageSize },
        signal,
      });
    return response.data;
  }

  public async getUserConversationHistory(organizationId: string, agentId: string, userId: string): Promise<ConversationMessage[]> {
    const response = await apiChannelsClient.get('/conversation/history', {
      headers: this.getHeaders(organizationId),
      params: { agentId, userId },
    });
    return response.data;
  }

  public async getChatMode(organizationId: string, agentId: string, userId: string, signal?: AbortSignal): Promise<ChatMode | null> {
    const response = await apiChannelsClientV2.get(`/agents/${agentId}/chat-mode`, {
      headers: this.getHeaders(organizationId),
      params: { user_id: userId },
      signal,
    });
    if (response.data === '') {
      return null;
    }
    return response.data;
  }

  public async setChatMode(organizationId: string, agentId: string, userId: string, isManualMode: boolean): Promise<void> {
    await apiChannelsClientV2.post(`/agents/${agentId}/chat-mode`,
      { userId, isManualMode },
      {
        headers: this.getHeaders(organizationId),
      }
    );
  }

  public async getAllUsersACL(organizationId: string): Promise<UserACL[]> {
    const response = await apiClient.get('/user-acl/all', {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async getUserACL(organizationId: string, user: string): Promise<UserACL> {
    const response = await apiClient.get(`/user-acl/${user}`,
      {
        headers: this.getHeaders(organizationId),
      });
    return response.data;
  }

  public async createUserACL(organizationId: string, acl: UserACLPostReq): Promise<UserACL> {
    const response = await apiClient.post('/user-acl', acl, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async updateUserACL(organizationId: string, user: string, acl: UserACLPutReq): Promise<UserACL> {
    const response = await apiClient.put(`/user-acl/${user}`, acl, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async deleteUserACL(organizationId: string, user: string): Promise<void> {
    await apiClient.delete(`/user-acl/${user}`, {
      headers: this.getHeaders(organizationId),
    });
  }

  public async getRoles(organizationId: string): Promise<AidaOrganizationRole[]> {
    const response = await apiClient.get(`/organizations/${organizationId}/roles`, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async createRole(organizationId: string, role: AidaOrganizationRolePostReq): Promise<AidaOrganizationRole> {
    const response = await apiClient.post(`/organizations/${organizationId}/roles`, role, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async getRoleById(organizationId: string, roleId: string): Promise<AidaOrganizationRole> {
    const response = await apiClient.get(`/organizations/${organizationId}/roles/${roleId}`, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async updateRole(organizationId: string, roleId: string, role: AidaOrganizationRolePutReq): Promise<AidaOrganizationRole> {
    const response = await apiClient.put(`/organizations/${organizationId}/roles/${roleId}`, role, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async deleteRole(organizationId: string, roleId: string): Promise<void> {
    await apiClient.delete(`/organizations/${organizationId}/roles/${roleId}`, {
      headers: this.getHeaders(organizationId),
    });
  }

  public async getQuotas(organizationId: string): Promise<AidaQuotaOrganization[]> {
    const response = await apiClient.get(`/organizations/${organizationId}/quotas`, {
      headers: this.getHeaders(organizationId),
    });
    return response.data;
  }

  public async publicConversation(organizationId: string, agentId: string, data: AidaChatRequestV2): Promise<AidaChatResponse> {
    const response = await apiClient.post(`/conversation/${agentId}`, data, {
      headers: this.getPublicHeaders(organizationId),
    });
    return response.data;
  }
}

export default ApiClient.getInstance();
